import React, { useState, useEffect } from 'react'
import { useHospitalSelection } from '../../hooks/useHospitalSelection'
import { useHospitalData } from '../../hooks/useHospitalData'
import { sortTimePeriods } from '../../utils/exportUtils'
import DonutChart from '../../components/Charts/DonutChart'
import BarChart from '../../components/Charts/BarChart'
import GaugeChart from '../../components/Charts/GaugeChart'
import LoadingNotice from '../../components/LoadingNotice/LoadingNotice'
import { createChartExportHandlers, createMenuToggle, setupMenuClickOutside, getDataDueThroughDate } from '../../utils/exportUtils'
import { generatePDFIds } from '../../utils/pdfIdHelper'
import './StatusAtGlance.css'

const StatusAtGlance: React.FC = () => {
    const { selectedHospitalIds, isMultiSelect } = useHospitalSelection()

    const { data: structureData, loading: structureLoading, initialLoading: structureInitialLoading, error: structureError } = useHospitalData(
        '/api/pmh/Status/structure_measures_status_count',
        selectedHospitalIds
    )
    const { data: providerEdData, loading: providerEdLoading, initialLoading: providerEdInitialLoading, error: providerEdError } = useHospitalData(
        '/api/pmh/process/provider_education',
        selectedHospitalIds
    )
    const { data: patientEdData, loading: patientEdLoading, initialLoading: patientEdInitialLoading, error: patientEdError } = useHospitalData(
        '/api/pmh/process/patient_education',
        selectedHospitalIds
    )
    const { data: screeningData, loading: screeningLoading, initialLoading: screeningInitialLoading, error: screeningError } = useHospitalData(
        '/api/pmh/process/screening',
        selectedHospitalIds
    )
    const { data: treatmentData, loading: treatmentLoading, initialLoading: treatmentInitialLoading, error: treatmentError } = useHospitalData(
        '/api/pmh/outcome/pmhTreatment',
        selectedHospitalIds
    )
    const { data: followUpData, loading: followUpLoading, initialLoading: followUpInitialLoading, error: followUpError } = useHospitalData(
        '/api/pmh/outcome/pmhlinkage',
        selectedHospitalIds
    )
    const { data: postpartumData, loading: postpartumLoading, initialLoading: postpartumInitialLoading, error: postpartumError } = useHospitalData(
        '/api/pmh/outcome/obpostpartum',
        selectedHospitalIds
    )

    const [structureChart, setStructureChart] = useState<{ labels: string[]; series: number[] }>({ labels: [], series: [] })
    const [structureMeasures, setStructureMeasures] = useState<{ havenotstarted: string[]; workingonit: string[]; inplace: string[] }>({ havenotstarted: [], workingonit: [], inplace: [] })
    const [providerChart, setProviderChart] = useState<any>(null)
    const [patientChart, setPatientChart] = useState<any>(null)
    const [screeningChart, setScreeningChart] = useState<any>(null)
    const [gauges, setGauges] = useState({ treatment: 0, followup: 0, postpartum: 0 })
    const [lastReportDate, setLastReportDate] = useState<string>('Loading...')

    const expandMonthName = (periodStr: string): string => {
        if (!periodStr) return ""
        const monthMap: Record<string, string> = {
            Jan: "January", Feb: "February", Mar: "March", Apr: "April",
            May: "May", Jun: "June", Jul: "July", Aug: "August",
            Sep: "September", Oct: "October", Nov: "November", Dec: "December"
        }
        periodStr = periodStr.replace(/Baseline/i, "").trim()
        const [abbr, year] = periodStr.split(" ").filter(Boolean)
        if (abbr && year) {
            const fullMonth = monthMap[abbr] || abbr
            return `${fullMonth} ${year}`
        }
        return periodStr
    }

    const safeParseVal = (value: any): number | null => {
        if (value === null || value === undefined || value === '' || value === '""') return null
        if (value === 0 || value === "0") return 0
        const num = parseFloat(value)
        return isNaN(num) ? null : num
    }

    // Single select: from lastReportDate header. Multi-select: mode of structure data months.
    const getTargetMonth = (): string | null => {
        const monthAbbr: Record<string, string> = {
            January: 'Jan', February: 'Feb', March: 'Mar', April: 'Apr',
            May: 'May', June: 'Jun', July: 'Jul', August: 'Aug',
            September: 'Sep', October: 'Oct', November: 'Nov', December: 'Dec'
        }

        if (!isMultiSelect && lastReportDate && lastReportDate !== 'Loading...' && lastReportDate !== 'NA') {
            const parts = lastReportDate.split(' ')
            if (parts.length === 2 && monthAbbr[parts[0]]) {
                return `${monthAbbr[parts[0]]} ${parts[1]}`
            }
        }

        if (structureData) {
            const sRows = structureData?.response?.result?.rows || []
            const sCols = structureData?.response?.result?.column_order || []
            const sMonthIdx = sCols.indexOf('month')
            const sHospIdx = sCols.indexOf('HospitalId') !== -1 ? sCols.indexOf('HospitalId') : sCols.indexOf('hospital_id')
            if (sMonthIdx !== -1 && sRows.length > 0) {
                const hospitalsByMonth: Record<string, Set<string>> = {}
                sRows.forEach((r: any) => {
                    const m = String(r[sMonthIdx])
                    const h = String(r[sHospIdx])
                    if (!m || m === 'undefined') return
                    if (!hospitalsByMonth[m]) hospitalsByMonth[m] = new Set()
                    hospitalsByMonth[m].add(h)
                })
                let maxCount = 0
                let bestMonth: string | null = null
                Object.entries(hospitalsByMonth).forEach(([month, hospitals]) => {
                    if (hospitals.size > maxCount) {
                        maxCount = hospitals.size
                        bestMonth = month
                    }
                })
                return bestMonth
            }
        }

        return null
    }

    const getBaselineAndLatestAverages = (
        rows: any[][],
        columns: string[],
        monthCol: string,
        valueCols: string[]
    ): { baselineVals: number[], latestVals: number[] } => {
        const valueIndices = valueCols.map(c => columns.indexOf(c))
        const monthIdx = columns.indexOf(monthCol)
        const targetMonth = getTargetMonth()

        // Group all rows by time period (same as individual pages)
        const periodData: Record<string, (number | null)[][]> = {}

        rows.forEach(row => {
            const month = String(row[monthIdx])
            if (!periodData[month]) periodData[month] = []
            periodData[month].push(valueIndices.map(i => safeParseVal(row[i])))
        })

        const avgForPeriod = (period: string): number[] | null => {
            const hospitalValues = periodData[period]
            if (!hospitalValues || hospitalValues.length === 0) return null

            return Array.from({ length: valueCols.length }, (_, colIdx) => {
                const vals = hospitalValues
                    .map(arr => arr[colIdx])
                    .filter(v => v !== null) as number[]
                if (vals.length === 0) return 0
                return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10
            })
        }

        const baselinePeriod = Object.keys(periodData).find(p => /baseline/i.test(p))
        const baselineVals = baselinePeriod ? (avgForPeriod(baselinePeriod) || valueCols.map(() => 0)) : valueCols.map(() => 0)

        let latestPeriod: string | undefined
        if (targetMonth && periodData[targetMonth]) {
            latestPeriod = targetMonth
        } else {
            const nonBaselinePeriods = Object.keys(periodData).filter(p => !/baseline/i.test(p))
            const periodsWithData = nonBaselinePeriods.filter(period =>
                periodData[period].some(hospitalVals => hospitalVals.some(v => v !== null))
            )
            const sorted = sortTimePeriods([...periodsWithData], 'monthly')
            latestPeriod = sorted[sorted.length - 1]
        }

        const latestVals = latestPeriod ? (avgForPeriod(latestPeriod) || valueCols.map(() => 0)) : valueCols.map(() => 0)

        return { baselineVals, latestVals }
    }

    useEffect(() => {
        if (isMultiSelect) {
            setLastReportDate('Most recent available per site')
            return
        }

        if (structureLoading) {
            setLastReportDate('Loading...')
            return
        }

        if (!structureData) {
            setStructureChart({ labels: [], series: [] })
            setStructureMeasures({ havenotstarted: [], workingonit: [], inplace: [] })
            setLastReportDate("NA")
            return
        }

        const rows = structureData?.response?.result?.rows || []
        const columns = structureData?.response?.result?.column_order || []

        if (rows.length === 0) {
            // Set empty data - chart will show noData message
            setStructureChart({ labels: [], series: [] })
            setStructureMeasures({ havenotstarted: [], workingonit: [], inplace: [] })
            setLastReportDate("NA")
            return
        }

        const statusIndex = columns.indexOf('status')
        const percentageIndex = columns.indexOf('percentage')
        const measureListIndex = columns.indexOf('measure_list')
        const monthIndex = columns.indexOf('month')

        const percentages: number[] = []
        const labels: string[] = []
        const measures: { havenotstarted: string[]; workingonit: string[]; inplace: string[] } = { havenotstarted: [], workingonit: [], inplace: [] }

        rows.forEach((row: any) => {
            const status = String(row[statusIndex])
            const percentage = parseFloat(String(row[percentageIndex])) || 0
            const measureList = String(row[measureListIndex] || '')

            labels.push(status)
            percentages.push(percentage)

            const measureArray = measureList.split(',').map(m => m.trim()).filter(m => m)

            if (status.toLowerCase().includes("haven't started")) {
                measures.havenotstarted = measureArray
            } else if (status.toLowerCase().includes("working on it")) {
                measures.workingonit = measureArray
            } else if (status.toLowerCase().includes("in place")) {
                measures.inplace = measureArray
            }
        })

        setStructureChart({ labels, series: percentages })
        setStructureMeasures(measures)

        // Update last report date - set to NA if month column not found or no value
        if (monthIndex !== -1 && rows[0][monthIndex]) {
            const month = String(rows[0][monthIndex])
            const expandedMonth = expandMonthName(month)
            setLastReportDate(expandedMonth)
        } else {
            setLastReportDate("NA")
        }
    }, [structureData, structureLoading, isMultiSelect])

    // Process provider education data
    useEffect(() => {
        if (!providerEdData) {
            setProviderChart({ categories: [], series: [] })
            return
        }

        const rows = providerEdData?.response?.result?.rows || []
        const columns = providerEdData?.response?.result?.column_order || []

        if (rows.length === 0) {
            // Set empty data - chart will show noData message
            setProviderChart({ categories: [], series: [] })
            return
        }

        const reportTypeIdx = columns.indexOf('report_type')
        const monthlyRows = rows.filter((row: any[]) => row[reportTypeIdx] === 'Monthly')

        if (monthlyRows.length === 0) {
            setProviderChart({ categories: [], series: [] })
            return
        }

        const valueCols = ['percent_ob_education', 'percent_ob_nurse_education', 'percent_er_education', 'percent_er_nurse_education']
        const { baselineVals, latestVals } = getBaselineAndLatestAverages(monthlyRows, columns, 'Month', valueCols)

        setProviderChart({
            categories: ['OB Providers', 'OB Nurses', 'ER Providers', 'ER Nurses'],
            series: [
                { name: 'Baseline', data: baselineVals },
                { name: 'Last Reporting Month', data: latestVals }
            ]
        })
    }, [providerEdData, isMultiSelect, structureData, lastReportDate])

    // Process patient education data
    useEffect(() => {
        if (!patientEdData) {
            setPatientChart({ categories: [], series: [] })
            return
        }

        const rows = patientEdData?.response?.result?.rows || []
        const columns = patientEdData?.response?.result?.column_order || []

        if (rows.length === 0) {
            setPatientChart({ categories: [], series: [] })
            return
        }

        const reportTypeIdx = columns.indexOf('report_type')
        const stratIdx = columns.indexOf('stratification')
        const filtered = rows.filter((row: any[]) => row[reportTypeIdx] === 'Monthly' && row[stratIdx] === 'Overall')

        if (filtered.length === 0) {
            setPatientChart({ categories: [], series: [] })
            return
        }

        const valueCols = ['education_pct']
        const { baselineVals, latestVals } = getBaselineAndLatestAverages(filtered, columns, 'period_label', valueCols)

        setPatientChart({
            categories: ['PMH Conditions, MOMS Line, MAR Now'],
            series: [
                { name: 'Baseline', data: baselineVals },
                { name: 'Last Reporting Month', data: latestVals }
            ]
        })
    }, [patientEdData, isMultiSelect, structureData, lastReportDate])

    // Process screening data
    useEffect(() => {
        if (!screeningData) {
            setScreeningChart({ categories: [], series: [] })
            return
        }

        const rows = screeningData?.response?.result?.rows || []
        const columns = screeningData?.response?.result?.column_order || []

        if (rows.length === 0) {
            // Set empty data - chart will show noData message
            setScreeningChart({ categories: [], series: [] })
            return
        }

        const reportTypeIdx = columns.indexOf('report_type')
        const stratIdx = columns.indexOf('stratification')
        const stratValIdx = columns.indexOf('strat_value')
        const filtered = rows.filter((row: any[]) =>
            row[reportTypeIdx] === 'Monthly' &&
            row[stratIdx] === 'Overall' &&
            row[stratValIdx] === 'All Patients'
        )

        if (filtered.length === 0) {
            setScreeningChart({ categories: [], series: [] })
            return
        }

        const valueCols = ['anxiety_delivery_pct', 'depression_delivery_pct', 'anxiety_prenatal_pct', 'depression_prenatal_pct']
        const { baselineVals, latestVals } = getBaselineAndLatestAverages(filtered, columns, 'period_label', valueCols)

        setScreeningChart({
            categories: ['Anxiety Delivery', 'Depression Delivery', 'Anxiety Prenatal', 'Depression Prenatal'],
            series: [
                { name: 'Baseline', data: baselineVals },
                { name: 'Last Reporting Month', data: latestVals }
            ]
        })
    }, [screeningData, isMultiSelect, structureData, lastReportDate])

    const isMoreRecent = (timePeriod1: string, timePeriod2: string): boolean => {
        const parseDate = (period: string): Date => {
            if (!period) return new Date(0)
            period = period.replace(/Baseline/i, "").trim()
            const parts = period.split(" ").filter(Boolean)
            if (parts.length < 2) return new Date(0)

            const monthAbbr = parts[0]
            const year = parts[1]

            const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
            const monthIndex = monthNames.findIndex(m => m.toLowerCase() === monthAbbr.toLowerCase())

            if (monthIndex === -1 || isNaN(parseInt(year))) {
                return new Date(0)
            }

            return new Date(parseInt(year), monthIndex)
        }

        try {
            const date1 = parseDate(timePeriod1)
            const date2 = parseDate(timePeriod2)
            return date1 > date2
        } catch (e) {
            return timePeriod1 > timePeriod2
        }
    }

    useEffect(() => {
        const processGaugeData = (
            data: any,
            monthCol: string,
            pctCol: string,
            stratFilter?: { col: string, val: string }
        ): number => {
            if (!data) return 0
            const rows = data?.response?.result?.rows || []
            const columns = data?.response?.result?.column_order || []
            if (rows.length === 0) return 0

            const reportTypeIdx = columns.indexOf('report_type')
            let filtered = rows.filter((row: any[]) => row[reportTypeIdx] === 'Monthly')

            if (stratFilter) {
                const stratIdx = columns.indexOf(stratFilter.col)
                filtered = filtered.filter((row: any[]) => row[stratIdx] === stratFilter.val)
            }

            if (filtered.length === 0) return 0

            // Uses getBaselineAndLatestAverages which internally calls getTargetMonth()
            const { latestVals } = getBaselineAndLatestAverages(filtered, columns, monthCol, [pctCol])
            return latestVals[0]
        }

        setGauges({
            treatment: processGaugeData(treatmentData, 'Month', 'Pct_Med_Or_Therapy', { col: 'StratGroup', val: 'All' }),
            followup: processGaugeData(followUpData, 'Month', 'Pct_FollowUpScheduled', { col: 'StratGroup', val: 'All' }),
            postpartum: processGaugeData(postpartumData, 'Month', 'Pct_Scheduled')
        })

        if (isMultiSelect && treatmentData) {
            setLastReportDate('Most recent available per site')
        }
    }, [treatmentData, followUpData, postpartumData, isMultiSelect, structureData, lastReportDate])

    // Refs for charts and containers (for export functionality)
    const structureContainerRef = React.useRef<HTMLDivElement>(null)
    const structureChartRef = React.useRef<any>(null)
    const providerChartRef = React.useRef<any>(null)
    const providerContainerRef = React.useRef<HTMLDivElement>(null)
    const patientChartRef = React.useRef<any>(null)
    const patientContainerRef = React.useRef<HTMLDivElement>(null)
    const screeningChartRef = React.useRef<any>(null)
    const screeningContainerRef = React.useRef<HTMLDivElement>(null)
    const gauge1Ref = React.useRef<any>(null)
    const gauge1ContainerRef = React.useRef<HTMLDivElement>(null)
    const gauge2Ref = React.useRef<any>(null)
    const gauge2ContainerRef = React.useRef<HTMLDivElement>(null)
    const gauge3Ref = React.useRef<any>(null)
    const gauge3ContainerRef = React.useRef<HTMLDivElement>(null)

    const structureMenuId = 'export-menu-structure'
    const toggleStructureMenu = createMenuToggle(structureMenuId)
    const structureExports = createChartExportHandlers(structureContainerRef, 'Structure Measures', 'PMH_Structure_Measures', { fontBoost: true, cssContextClass: 'pmh-status-at-glance-page' })

    const providerMenuId = 'export-menu-provider'
    const toggleProviderMenu = createMenuToggle(providerMenuId)
    const providerExports = createChartExportHandlers(providerContainerRef, 'Clinical Team Education', 'Clinical_Team_Education', { fontBoost: true })

    const patientMenuId = 'export-menu-patient'
    const togglePatientMenu = createMenuToggle(patientMenuId)
    const patientExports = createChartExportHandlers(patientContainerRef, 'Patient Education', 'Patient_Education', { fontBoost: true })

    const screeningMenuId = 'export-menu-screening'
    const toggleScreeningMenu = createMenuToggle(screeningMenuId)
    const screeningExports = createChartExportHandlers(screeningContainerRef, 'PMH Screening', 'PMH_Screening', { fontBoost: true })

    const gauge1MenuId = 'export-menu-gauge1'
    const toggleGauge1Menu = createMenuToggle(gauge1MenuId)
    const gauge1Exports = createChartExportHandlers(gauge1ContainerRef, 'Treatment PMH', 'Treatment_PMH')

    const gauge2MenuId = 'export-menu-gauge2'
    const toggleGauge2Menu = createMenuToggle(gauge2MenuId)
    const gauge2Exports = createChartExportHandlers(gauge2ContainerRef, 'Follow Up PMH', 'Follow_Up_PMH')

    const gauge3MenuId = 'export-menu-gauge3'
    const toggleGauge3Menu = createMenuToggle(gauge3MenuId)
    const gauge3Exports = createChartExportHandlers(gauge3ContainerRef, 'Postpartum PMH', 'Postpartum_PMH')

    React.useEffect(() => {
        const cleanups = [
            setupMenuClickOutside(() => structureExports.closeMenu(structureMenuId)),
            setupMenuClickOutside(() => providerExports.closeMenu(providerMenuId)),
            setupMenuClickOutside(() => patientExports.closeMenu(patientMenuId)),
            setupMenuClickOutside(() => screeningExports.closeMenu(screeningMenuId)),
            setupMenuClickOutside(() => gauge1Exports.closeMenu(gauge1MenuId)),
            setupMenuClickOutside(() => gauge2Exports.closeMenu(gauge2MenuId)),
            setupMenuClickOutside(() => gauge3Exports.closeMenu(gauge3MenuId)),
        ]
        return () => cleanups.forEach(c => c())
    }, [])

    const handleDownloadStructureCSV = () => {
        const categories = ["Haven't Started", "Working on It", "In Place"];
        const series = structureChart.series;
        const measures = [
            structureMeasures.havenotstarted.join(', '),
            structureMeasures.workingonit.join(', '),
            structureMeasures.inplace.join(', ')
        ];

        let csvContent = "data:text/csv;charset=utf-8,Category,Percentage,Measures\n";

        categories.forEach((cat, i) => {
            const pct = series[i] || 0;
            const msr = measures[i] || '';
            // Escape quotes in measures
            const escapedMsr = msr.replace(/"/g, '""');
            csvContent += `"${cat}",${pct}%,"${escapedMsr}"\n`;
        });

        csvContent += `\nLast Updated,${lastReportDate}\n`;

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "structure_measures.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleDownloadGaugeCSV = (label: string, value: number, goal: number = 80) => {
        const status = value >= goal ? 'Met Goal' : 'Below Goal';
        const csvContent = "data:text/csv;charset=utf-8," +
            "Measure,Percentage,Goal\n" +
            `"${label}",${value.toFixed(0)}%,${goal}%\n` +
            `\nStatus,${status}`;

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `${label.replace(/ /g, '_')}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };



    const isInitialLoading = structureInitialLoading || providerEdInitialLoading || patientEdInitialLoading ||
        screeningInitialLoading || treatmentInitialLoading || followUpInitialLoading || postpartumInitialLoading

    const hasError = structureError || providerEdError || patientEdError || screeningError ||
        treatmentError || followUpError || postpartumError

    if (isInitialLoading) {
        return <LoadingNotice title="Loading status data..." />
    }

    if (hasError) {
        return (
            <div className="pmh-status-at-glance-page">
                <div className="error-container" style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '400px',
                    textAlign: 'center',
                    padding: '40px'
                }}>
                    <div style={{
                        fontSize: '48px',
                        marginBottom: '20px',
                        color: '#dc3545'
                    }}>⚠️</div>
                    <h2 style={{
                        color: '#dc3545',
                        marginBottom: '10px',
                        fontSize: '24px'
                    }}>Failed to fetch data</h2>
                    <p style={{
                        color: '#666',
                        fontSize: '16px',
                        marginBottom: '20px'
                    }}>Unable to load status data. Please check your connection and try again.</p>
                    <button
                        onClick={() => window.location.reload()}
                        style={{
                            padding: '10px 24px',
                            fontSize: '14px',
                            backgroundColor: '#18457A',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer'
                        }}
                    >
                        Retry
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div id={generatePDFIds.dashboard()} className="pmh-status-at-glance-page">
            <div className="header-wrapper">
                <div className="header-left">
                    <h1 className="header-title">Status at Glance</h1>
                    <div className="header-info">
                        <p className="header-sub">Your hospital last reported data for: <strong>{lastReportDate}</strong></p>
                        <p className="header-sub">Current data is due through: <strong>{getDataDueThroughDate()}</strong></p>
                    </div>
                </div>
            </div>

            {/* Structure Measures */}
            {!isMultiSelect && (
                <section id={generatePDFIds.chart(1)} ref={structureContainerRef} className="card">
                    <div className="card-header">
                        <h3 className="export-ignore2">Structure Measures</h3>
                        <div className="export-menu export-ignore">
                            <button className="hamburger" onClick={toggleStructureMenu}>&#9776;</button>
                            <div id={structureMenuId} className="export-dropdown">
                                <div onClick={() => { structureExports.handleDownloadSVG(structureMenuId); }}>Download SVG</div>
                                <div onClick={() => { structureExports.handleDownloadPNG(structureMenuId); }}>Download PNG</div>
                                <div onClick={() => { handleDownloadStructureCSV(); structureExports.closeMenu(structureMenuId); }}>Download CSV</div>
                            </div>
                        </div>
                    </div>
                    <div className="structure-container">
                        <div className="structure-grid">
                            <div className="legend-section">
                                <div className="legend-row">
                                    <div className="legend-item">
                                        <div className="legend-content">
                                            <div className="legend-title">
                                                <span className="legend-dot orange"></span>
                                                Haven't started:
                                            </div>
                                            <div className="measures-list">
                                                {structureMeasures.havenotstarted.length > 0
                                                    ? structureMeasures.havenotstarted.map((m, i) => <div key={i} className="measure-item">{m}</div>)
                                                    : 'None'}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="legend-item">
                                        <div className="legend-content">
                                            <div className="legend-title">
                                                <span className="legend-dot yellow"></span>
                                                Working on it:
                                            </div>
                                            <div className="measures-list">
                                                {structureMeasures.workingonit.length > 0
                                                    ? structureMeasures.workingonit.map((m, i) => <div key={i} className="measure-item">{m}</div>)
                                                    : 'None'}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="legend-item">
                                        <div className="legend-content">
                                            <div className="legend-title">
                                                <span className="legend-dot green"></span>
                                                In place:
                                            </div>
                                            <div className="measures-list">
                                                {structureMeasures.inplace.length > 0
                                                    ? structureMeasures.inplace.map((m, i) => <div key={i} className="measure-item">{m}</div>)
                                                    : 'None'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="structure-donut-container">
                                <DonutChart
                                    ref={structureChartRef}
                                    labels={structureChart.labels}
                                    series={structureChart.series}
                                    colors={['#ff0000', '#F9E15B', '#429c75']}
                                    height={240}
                                    showLegend={false}
                                    type="pie"
                                    exportFilename="PMH_Structure_Measures"
                                    isLoading={structureLoading}
                                />
                            </div>
                        </div>
                        <p className="structure-note">Data from hospital level Data form</p>
                    </div>
                </section>
            )}

            {/* Process Measures */}
            <section className="card">
                <div className="card-header">
                    <h3>Process Measures</h3>
                </div>
                <div className="process-wrapper">
                    <div className="chart-row">
                            <div id={generatePDFIds.chart(2)} className="chart-box" ref={providerContainerRef}>
                                <div className="chart-title-wrapper">
                                    <div className="chart-title">Clinical Team Education</div>
                                    <div className="export-menu export-ignore">
                                        <button className="hamburger" onClick={toggleProviderMenu}>&#9776;</button>
                                        <div id={providerMenuId} className="export-dropdown">
                                            <div onClick={() => { providerExports.handleDownloadSVG(providerMenuId); }}>Download SVG</div>
                                            <div onClick={() => { providerExports.handleDownloadPNG(providerMenuId); }}>Download PNG</div>
                                            <div onClick={() => { providerChartRef.current?.exportToCSV(); providerExports.closeMenu(providerMenuId); }}>Download CSV</div>
                                        </div>
                                    </div>
                                </div>
                                {providerChart && providerChart.categories.length > 0 ? (
                                    <BarChart
                                        ref={providerChartRef}
                                        categories={providerChart.categories}
                                        series={providerChart.series}
                                        yAxisMax={100}
                                        yAxisMin={0}
                                        yAxisTickAmount={5}
                                        height={350}
                                        colors={['#72b1df', '#18457A']}
                                        goalLine={80}
                                        goalLabel="Goal ≥ 80%"
                                        showToolbar={true}
                                        plotOptionsBar={{ borderRadius: 0 }}
                                        legendPosition="top"
                                        legendAlign="center"
                                        exportFilename="Clinical_Team_Education"
                                        isLoading={providerEdLoading}
                                        zeroStateTextColor="#000"
                                        legendFontSize="13px"
                                        legendFontWeight="bold"
                                    />
                                ) : !providerEdLoading && (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: '#888', fontSize: '12px', fontFamily: 'Helvetica, Arial, sans-serif' }}>No Data Available</div>
                                )}
                            </div>
                            <div id={generatePDFIds.chart(3)} className="chart-box" ref={patientContainerRef}>
                                <div className="chart-title-wrapper">
                                    <div className="chart-title">Patient Education</div>
                                    <div className="export-menu export-ignore">
                                        <button className="hamburger" onClick={togglePatientMenu}>&#9776;</button>
                                        <div id={patientMenuId} className="export-dropdown">
                                            <div onClick={() => { patientExports.handleDownloadSVG(patientMenuId); }}>Download SVG</div>
                                            <div onClick={() => { patientExports.handleDownloadPNG(patientMenuId); }}>Download PNG</div>
                                            <div onClick={() => { patientChartRef.current?.exportToCSV(); patientExports.closeMenu(patientMenuId); }}>Download CSV</div>
                                        </div>
                                    </div>
                                </div>
                                {patientChart && patientChart.categories.length > 0 ? (
                                    <BarChart
                                        ref={patientChartRef}
                                        categories={patientChart.categories}
                                        series={patientChart.series}
                                        yAxisMax={100}
                                        yAxisMin={0}
                                        yAxisTickAmount={5}
                                        height={350}
                                        colors={['#72b1df', '#18457A']}
                                        goalLine={80}
                                        goalLabel="Goal ≥ 80%"
                                        showToolbar={true}
                                        plotOptionsBar={{ borderRadius: 0 }}
                                        legendPosition="top"
                                        legendAlign="center"
                                        exportFilename="Patient_Education"
                                        isLoading={patientEdLoading}
                                        zeroStateTextColor="#000"
                                        legendFontSize="13px"
                                        legendFontWeight="bold"
                                        columnWidth="40"
                                    />
                                ) : !patientEdLoading && (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: '#888', fontSize: '12px', fontFamily: 'Helvetica, Arial, sans-serif' }}>No Data Available</div>
                                )}
                            </div>
                    </div>
                        <div id={generatePDFIds.chart(4)} className="chart-box full-width" ref={screeningContainerRef}>
                            <div className="chart-title-wrapper">
                                <div className="chart-title">PMH Screening</div>
                                <div className="export-menu export-ignore">
                                    <button className="hamburger" onClick={toggleScreeningMenu}>&#9776;</button>
                                    <div id={screeningMenuId} className="export-dropdown">
                                        <div onClick={() => { screeningExports.handleDownloadSVG(screeningMenuId); }}>Download SVG</div>
                                        <div onClick={() => { screeningExports.handleDownloadPNG(screeningMenuId); }}>Download PNG</div>
                                        <div onClick={() => { screeningChartRef.current?.exportToCSV(); screeningExports.closeMenu(screeningMenuId); }}>Download CSV</div>
                                    </div>
                                </div>
                            </div>
                            {screeningChart && screeningChart.categories.length > 0 ? (
                                <BarChart
                                    ref={screeningChartRef}
                                    categories={screeningChart.categories}
                                    series={screeningChart.series}
                                    height={350}
                                    yAxisMax={100}
                                    yAxisMin={0}
                                    yAxisTickAmount={5}
                                    colors={['#72b1df', '#18457A']}
                                    goalLine={80}
                                    goalLabel="Goal ≥ 80%"
                                    showToolbar={true}
                                    plotOptionsBar={{ borderRadius: 0 }}
                                    legendPosition="top"
                                    legendAlign="center"
                                    exportFilename="PMH_Screening"
                                    isLoading={screeningLoading}
                                    zeroStateTextColor="#000"
                                    legendFontSize="13px"
                                    legendFontWeight="bold"
                                />
                            ) : !screeningLoading && (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: '#888', fontSize: '12px', fontFamily: 'Helvetica, Arial, sans-serif' }}>No Data Available</div>
                            )}
                        </div>
                    <p className="header-note">Data from hospital level Data form</p>
                </div>
            </section>

            {/* Outcome Measures */}
            <section className="outcome-card">
                <div className="outcome-header">
                    <h3>Outcome Measures</h3>
                </div>
                <p className="sub-heading">Treatment & Followup</p>
                <div className="gauge-grid">
                    <div id={generatePDFIds.chart(5)} className="gauge-container" ref={gauge1ContainerRef}>
                        <div className="gauge-header">
                            <div className="export-menu export-ignore">
                                <button className="hamburger" onClick={toggleGauge1Menu}>&#9776;</button>
                                <div id={gauge1MenuId} className="export-dropdown">
                                    <div onClick={() => { gauge1Exports.handleDownloadSVG(gauge1MenuId); }}>Download SVG</div>
                                    <div onClick={() => { gauge1Exports.handleDownloadPNG(gauge1MenuId); }}>Download PNG</div>
                                    <div onClick={() => { handleDownloadGaugeCSV('Treatment PMH', gauges.treatment); gauge1Exports.closeMenu(gauge1MenuId); }}>Download CSV</div>
                                </div>
                            </div>
                        </div>
                        <GaugeChart
                            value={gauges.treatment}
                            max={100}
                            label="Provide treatment for PMH condition prenatally or by delivery discharge"
                            goal={80}
                            color="#A9D3FF"
                            isLoading={treatmentLoading}
                        />
                    </div>
                    <div id={generatePDFIds.chart(6)} className="gauge-container" ref={gauge2ContainerRef}>
                        <div className="gauge-header">
                            <div className="export-menu export-ignore">
                                <button className="hamburger" onClick={toggleGauge2Menu}>&#9776;</button>
                                <div id={gauge2MenuId} className="export-dropdown">
                                    <div onClick={() => { gauge2Exports.handleDownloadSVG(gauge2MenuId); }}>Download SVG</div>
                                    <div onClick={() => { gauge2Exports.handleDownloadPNG(gauge2MenuId); }}>Download PNG</div>
                                    <div onClick={() => { handleDownloadGaugeCSV('Follow Up PMH', gauges.followup); gauge2Exports.closeMenu(gauge2MenuId); }}>Download CSV</div>
                                </div>
                            </div>
                        </div>
                        <GaugeChart
                            value={gauges.followup}
                            max={100}
                            label="Linkage to follow up behavioral healthcare"
                            goal={80}
                            color="#A9D3FF"
                            isLoading={followUpLoading}
                        />
                    </div>
                </div>
                    <div id={generatePDFIds.chart(7)} className="gauge-container full-width" ref={gauge3ContainerRef}>
                        <div className="gauge-header">
                            <div className="export-menu export-ignore">
                                <button className="hamburger" onClick={toggleGauge3Menu}>&#9776;</button>
                                <div id={gauge3MenuId} className="export-dropdown">
                                    <div onClick={() => { gauge3Exports.handleDownloadSVG(gauge3MenuId); }}>Download SVG</div>
                                    <div onClick={() => { gauge3Exports.handleDownloadPNG(gauge3MenuId); }}>Download PNG</div>
                                    <div onClick={() => { handleDownloadGaugeCSV('Postpartum PMH', gauges.postpartum); gauge3Exports.closeMenu(gauge3MenuId); }}>Download CSV</div>
                                </div>
                            </div>
                        </div>
                        <GaugeChart
                            value={gauges.postpartum}
                            max={100}
                            label="Patients scheduled for an early postpartum follow-up visit with a clinical provider (by 2 weeks postpartum)"
                            goal={80}
                            color="#A9D3FF"
                            isLoading={postpartumLoading}
                        />
                    </div>
                <p className="outcome-note">Data from patient level data form</p>
            </section>
        </div>
    )
}

export default StatusAtGlance
